// src/shared/services/httpClient.ts

import { ApiResponse } from '../../types/api';
import { API_BASE_URL, AUTH_CONFIG, API_CONFIG } from './apiConfig';

/**
 * Cliente HTTP centralizado para todas las peticiones a la API
 * Maneja autenticación, errores, timeouts y reintentos
 */
class HttpClient {
  private baseURL: string;
  private requestInterceptors: Array<(config: RequestInit) => RequestInit> = [];
  private responseInterceptors: Array<(response: Response) => Response> = [];

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Obtener token JWT del localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  }

  /**
   * Limpiar datos de autenticación
   */
  private clearAuthData(): void {
    localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  }

  /**
   * Construir headers con autenticación
   */
  private buildHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...customHeaders,
    };

    // Agregar token de autorización si existe
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Petición base con manejo de errores y reintentos
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}${endpoint}`;

      const headers = this.buildHeaders(options.headers as Record<string, string>);

      const config: RequestInit = {
        ...options,
        headers,
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      };

      // console.log(`🚀 [${config.method || 'GET'}] ${url}`);

      const response = await fetch(url, config);

      // Leer respuesta
      const contentType = response.headers.get('content-type');
      let responseData: any;

      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = { message: text };
      }

      // Manejo de errores HTTP
      if (!response.ok) {
        // console.error(`❌ [${response.status}] ${url}`, responseData);

        // Token inválido o expirado
        if (response.status === 401) {
          this.clearAuthData();
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }

        // Reintentar en errores de servidor (500+)
        if (response.status >= 500 && retryCount < API_CONFIG.MAX_RETRIES) {
          // console.log(`🔄 Reintentando... (${retryCount + 1}/${API_CONFIG.MAX_RETRIES})`);
          await this.delay(1000 * (retryCount + 1)); // Backoff exponencial
          return this.request<T>(endpoint, options, retryCount + 1);
        }

        return {
          success: false,
          message: responseData?.message || `Error HTTP ${response.status}`,
          data: null,
          status: response.status,
        };
      }

      // console.log(`✅ [${response.status}] ${url}`);

      // Respuesta exitosa
      if (responseData && typeof responseData === 'object' && 'success' in responseData) {
        return responseData as ApiResponse<T>;
      }

      // Envolver respuesta simple en formato ApiResponse
      return {
        success: true,
        message: 'Operación exitosa',
        data: responseData as T,
        status: response.status,
      };

    } catch (error: any) {
      // console.error('❌ Network Error:', error);

      // Reintentar en errores de red
      if (retryCount < API_CONFIG.MAX_RETRIES && error.name !== 'AbortError') {
        // console.log(`🔄 Reintentando... (${retryCount + 1}/${API_CONFIG.MAX_RETRIES})`);
        await this.delay(1000 * (retryCount + 1));
        return this.request<T>(endpoint, options, retryCount + 1);
      }

      return {
        success: false,
        message: error.name === 'AbortError'
          ? 'Tiempo de espera agotado'
          : error.message || 'Error de conexión',
        data: null,
        status: 0,
      };
    }
  }

  /**
   * Delay helper para reintentos
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * GET Request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    let url = endpoint;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.request<T>(url, { method: 'GET' });
  }

  /**
   * POST Request (JSON)
   */
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * POST Request (FormData) - Para subir archivos
   */
  async postFormData<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    // No establecer Content-Type para FormData (el browser lo hace automáticamente)
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * PUT Request
   */
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
 * DELETE Request (con body opcional)
 */
async delete<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
  return this.request<T>(endpoint, {
    method: 'DELETE',
    headers: data ? {
      'Content-Type': 'application/json',
    } : undefined,
    body: data ? JSON.stringify(data) : undefined,
  });
}

  /**
   * Subir imagen (método específico para competencias)
   */
  async uploadImage<T>(
    endpoint: string,
    imageFile: File,
    additionalData: Record<string, any>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('image', imageFile);

    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    return this.postFormData<T>(endpoint, formData);
  }

  /**
   * Descargar archivo
   */
  async downloadFile(endpoint: string, filename: string): Promise<boolean> {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const token = this.getAuthToken();

      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      // console.error('Error descargando archivo:', error);
      return false;
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/health.php`);
      return response.ok;
    } catch {
      return false;
    }
  }
}

export const httpClient = new HttpClient();